/**
 * pgvector strategy.
 *
 * Postgres' pgvector extension is the one engine where the *same* SQL serves
 * brute-force and ANN: `ORDER BY embedding <=> q LIMIT k` scans sequentially
 * with no index and is rewritten to an HNSW/IVFFlat scan the moment a matching
 * index exists — the planner picks it up automatically. So `buildSearch` never
 * branches on `slot.indexType`; it emits one relevance scan and lets Postgres
 * decide. (Contrast libSQL/sqlite-vec, whose ANN needs distinct syntax.)
 *
 * Storage is per-`(nodeKind, fieldPath)`: a `vector(N)` column carrying the
 * field's fixed dimension `N`, with the operator-class-typed ANN index folded
 * into the same table contribution so materialization builds table + index
 * together. The typed column means the index does not need the legacy
 * `::vector(N)` cast-in-index escape hatch the old shared table relied on.
 *
 * Metric support is the full pgvector set — `cosine`, `l2`, `inner_product` —
 * advertised as data on `capabilities`, mirroring `vector_cosine_ops` /
 * `vector_l2_ops` / `vector_ip_ops` and the `<=>` / `<->` / `<#>` operators.
 *
 * Mirrors the `libsqlVectorStrategy` / `sqliteVecStrategy` structure while
 * letting Postgres' planner pick the ANN index when one exists.
 */
import { type StrategyTableContribution } from "../../../backend/table-contribution";
import {
  type DeleteEmbeddingParams,
  type UpsertEmbeddingBatchParams,
  type UpsertEmbeddingParams,
  type VectorCapabilities,
  type VectorMetric,
  type VectorSearchParams,
} from "../../../backend/types";
import { sql, type SqlFragment } from "../../sql-fragment";
import {
  assertFiniteEmbedding,
  quoteIdentifier,
  VECTOR_CONTRIBUTION_PREFIX,
  vectorMinScoreCondition,
  vectorPhysicalName,
  vectorScoreExpression,
  type VectorSlot,
  type VectorStrategy,
} from "../vector-strategy";
import { vectorPageClause } from "./pagination";

/** Physical-name prefixes for the per-field table and its ANN index. */
const TABLE_PREFIX = "tg_vec";
const INDEX_PREFIX = "tg_vecidx";

/**
 * pgvector defines `hnsw.ef_search` as an integer GUC with a valid range
 * of 1..1000; `SET hnsw.ef_search = 1001` errors at the server. The
 * backend validates a per-search `efSearch` against this ceiling before
 * inlining it into `SET LOCAL`, surfacing a clear message instead of a raw
 * pgvector error.
 */
export const MAX_HNSW_EF_SEARCH = 1000;

/**
 * Validates a per-search `efSearch` override against pgvector's
 * `hnsw.ef_search` valid range. `undefined` (no override) is accepted.
 */
export function assertPgvectorEfSearch(efSearch?: number): void {
  if (efSearch === undefined) return;
  if (!Number.isInteger(efSearch) || efSearch <= 0) {
    throw new Error(`efSearch must be a positive integer, got: ${efSearch}`);
  }
  if (efSearch > MAX_HNSW_EF_SEARCH) {
    throw new RangeError(
      `efSearch must be ≤ ${MAX_HNSW_EF_SEARCH} (pgvector's hnsw.ef_search ` +
        `valid range is 1..${MAX_HNSW_EF_SEARCH}), got: ${efSearch}`,
    );
  }
}

/**
 * pgvector's `vector` type caps at 16,000 dimensions for a stored column
 * (2,000 for an indexed column, but the column-type ceiling is what governs
 * storage; recall degradation beyond 2,000 is a tuning concern, not a hard
 * limit advertised here).
 */
const PGVECTOR_MAX_DIMENSIONS = 16_000;

/** Default HNSW `m` (max connections per layer) — pgvector's own default. */
const DEFAULT_HNSW_M = 16;
/** Default HNSW `ef_construction` (build candidate list) — pgvector's own default. */
const DEFAULT_HNSW_EF_CONSTRUCTION = 64;
/** Default IVFFlat `lists` (inverted lists) — pgvector's own default. */
const DEFAULT_IVFFLAT_LISTS = 100;

const PGVECTOR_CAPABILITIES: VectorCapabilities = {
  supported: true,
  metrics: ["cosine", "l2", "inner_product"],
  indexTypes: ["hnsw", "ivfflat", "none"],
  maxDimensions: PGVECTOR_MAX_DIMENSIONS,
  // The backend probes `pg_extension.extversion` once and turns on
  // `hnsw.iterative_scan` / `ivfflat.iterative_scan` when pgvector >= 0.8, so a
  // filtered scan re-enters the index for more candidates.
  //
  // NOT a full-page guarantee, on either axis. The iterative scan stops at
  // `hnsw.max_scan_tuples` / `ivfflat.max_probes`; and on pgvector < 0.8 there
  // is no iterative scan at all, so the search stays `ef_search`-bounded and
  // behaves like a post-filter. A statically-declared mode cannot express
  // either, which is why the guarantee is its own field.
  filteredApproximateSearch: {
    mode: "iterative-scan",
    guaranteesFullPage: false,
  },
  // The one bundled engine with a real per-search frontier: `hnsw.ef_search`
  // sizes the HNSW candidate list. IVFFlat has no equivalent (`ivfflat.probes`
  // is a different structure's knob and is not what `efSearch` means), and the
  // GUC is applied with `SET LOCAL` inside the search's own transaction so
  // concurrent searches cannot inherit each other's override.
  searchFrontierTuning: {
    tunable: true,
    parameter: "hnsw.ef_search",
    indexType: "hnsw",
    requiresTransactionScope: true,
  },
};

/** Whether a slot's declared index type materializes a real pgvector ANN index. */
function usesAnnIndex(slot: VectorSlot): boolean {
  return slot.indexType === "hnsw" || slot.indexType === "ivfflat";
}

/**
 * pgvector operator class for a metric's ANN index — `vector_cosine_ops` /
 * `vector_l2_ops` / `vector_ip_ops`, mirroring the `<=>` / `<->` / `<#>`
 * distance operators this strategy emits.
 */
function operatorClass(metric: VectorMetric): string {
  switch (metric) {
    case "cosine": {
      return "vector_cosine_ops";
    }
    case "l2": {
      return "vector_l2_ops";
    }
    case "inner_product": {
      return "vector_ip_ops";
    }
    default: {
      const _exhaustive: never = metric;
      throw new Error(`Unsupported vector metric: ${String(_exhaustive)}`);
    }
  }
}

/**
 * Formats an embedding as a `::vector` literal. The value is bound as a
 * parameter (not interpolated) — Postgres parses the `[a,b,c]` text and the
 * cast types it. Finiteness is validated first so a NaN names its index.
 */
function vectorLiteral(
  embedding: readonly number[],
  name: string,
): SqlFragment {
  assertFiniteEmbedding(embedding, name);
  return sql`${`[${embedding.join(",")}]`}::vector`;
}

function distanceExpression(
  embeddingColumn: SqlFragment,
  queryEmbedding: readonly number[],
  metric: VectorMetric,
): SqlFragment {
  const query = vectorLiteral(queryEmbedding, "queryEmbedding");
  switch (metric) {
    case "cosine": {
      return sql`(${embeddingColumn} <=> ${query})`;
    }
    case "l2": {
      return sql`(${embeddingColumn} <-> ${query})`;
    }
    case "inner_product": {
      return sql`(${embeddingColumn} <#> ${query})`;
    }
    default: {
      const _exhaustive: never = metric;
      throw new Error(`Unsupported vector metric: ${String(_exhaustive)}`);
    }
  }
}

function tableName(
  graphId: string,
  nodeKind: string,
  fieldPath: string,
): string {
  return vectorPhysicalName(TABLE_PREFIX, graphId, nodeKind, fieldPath);
}

export const pgvectorStrategy: VectorStrategy = {
  name: "pgvector",
  capabilities: PGVECTOR_CAPABILITIES,

  tableName,

  ownedTables(slot): readonly StrategyTableContribution[] {
    const table = tableName(slot.graphId, slot.nodeKind, slot.fieldPath);
    const name = quoteIdentifier(table);

    // No standalone graph_id index: the PRIMARY KEY (graph_id, node_id) already
    // covers `WHERE graph_id = ?` via its leading column, so a separate index
    // would be pure write amplification.
    const createDdl = [
      `CREATE TABLE IF NOT EXISTS ${name} (
  "graph_id" TEXT NOT NULL,
  "node_id" TEXT NOT NULL,
  "embedding" vector(${slot.dimensions}) NOT NULL,
  "created_at" TIMESTAMPTZ NOT NULL,
  "updated_at" TIMESTAMPTZ NOT NULL,
  PRIMARY KEY ("graph_id", "node_id")
);`,
    ];

    // The HNSW/IVFFlat index is intentionally NOT created here. pgvector
    // similarity SQL is planner-driven (`ORDER BY embedding <=> q LIMIT k`
    // uses the index when present, sequential-scans when not), so the index
    // is a pure materialization concern: `buildCreateIndex` builds it through
    // `materializeIndexes()` with the field's declared `m`/`ef_construction`/
    // `lists`. Building it eagerly here would bake in default tuning (the
    // write-ensure slot has no `indexParams`) and `CREATE INDEX IF NOT EXISTS`
    // would then mask the tuned index materialization would emit.

    return [
      {
        scope: "graph",
        logicalName: `${VECTOR_CONTRIBUTION_PREFIX}:${slot.nodeKind}.${slot.fieldPath}`,
        owner: "pgvector",
        tableName: table,
        createDdl,
        runtimeEnsure: true,
      },
    ];
  },

  buildUpsert(
    slot,
    params: UpsertEmbeddingParams,
    timestamp,
  ): readonly SqlFragment[] {
    const table = sql.identifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    const value = vectorLiteral(params.embedding, "embedding");
    return [
      sql`
        INSERT INTO ${table} ("graph_id", "node_id", "embedding", "created_at", "updated_at")
        VALUES (${params.graphId}, ${params.nodeId}, ${value}, ${timestamp}, ${timestamp})
        ON CONFLICT ("graph_id", "node_id")
        DO UPDATE SET "embedding" = EXCLUDED."embedding", "updated_at" = EXCLUDED."updated_at"
      `,
    ];
  },

  buildUpsertFromInsertedNode(slot, sourceAlias, embedding, timestamp) {
    const table = sql.identifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    const source = sql.identifier(sourceAlias);
    const sourceColumn = (name: string): SqlFragment =>
      sql`${source}.${sql.identifier(name)}`;
    const value = vectorLiteral(embedding, "embedding");

    return sql`
      INSERT INTO ${table}
        ("graph_id", "node_id", "embedding", "created_at", "updated_at")
      SELECT
        ${sourceColumn("graph_id")}, ${sourceColumn("id")},
        ${value}, ${timestamp}, ${timestamp}
      FROM ${source}
      ON CONFLICT ("graph_id", "node_id")
      DO UPDATE SET
        "embedding" = EXCLUDED."embedding",
        "updated_at" = EXCLUDED."updated_at"
      RETURNING 1
    `;
  },

  buildUpsertBatch(
    slot,
    params: UpsertEmbeddingBatchParams,
    timestamp,
  ): readonly SqlFragment[] {
    const table = sql.identifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    const valueRows = sql.join(
      params.rows.map(
        (row) =>
          sql`(${params.graphId}, ${row.nodeId}, ${vectorLiteral(row.embedding, "embedding")}, ${timestamp}, ${timestamp})`,
      ),
      sql`, `,
    );
    return [
      sql`
        INSERT INTO ${table} ("graph_id", "node_id", "embedding", "created_at", "updated_at")
        VALUES ${valueRows}
        ON CONFLICT ("graph_id", "node_id")
        DO UPDATE SET "embedding" = EXCLUDED."embedding", "updated_at" = EXCLUDED."updated_at"
      `,
    ];
  },

  buildDelete(slot, params: DeleteEmbeddingParams): readonly SqlFragment[] {
    const table = sql.identifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    return [
      sql`
        DELETE FROM ${table}
        WHERE "graph_id" = ${params.graphId} AND "node_id" = ${params.nodeId}
      `,
    ];
  },

  buildDeleteBatch(
    slot,
    params: Omit<DeleteEmbeddingParams, "nodeId"> &
      Readonly<{ nodeIds: readonly string[] }>,
  ): readonly SqlFragment[] {
    if (params.nodeIds.length === 0) return [];
    const table = sql.identifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    return [
      sql`DELETE FROM ${table} WHERE "graph_id" = ${params.graphId} AND "node_id" IN (${sql.join(
        params.nodeIds.map((nodeId) => sql`${nodeId}`),
        sql`, `,
      )})`,
    ];
  },

  buildSearch(
    slot,
    params: VectorSearchParams,
    candidates?: SqlFragment,
  ): SqlFragment {
    const table = sql.identifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    const embeddingColumn = sql`${table}."embedding"`;
    const distance = distanceExpression(
      embeddingColumn,
      params.queryEmbedding,
      params.metric,
    );
    const score = vectorScoreExpression(distance, params.metric);

    // Same SQL for brute-force and ANN: with a matching HNSW/IVFFlat index the
    // Postgres planner rewrites this `ORDER BY distance LIMIT k` into an index
    // scan automatically, so the strategy never branches on `slot.indexType`.
    const conditions: SqlFragment[] = [
      sql`${table}."graph_id" = ${params.graphId}`,
    ];
    if (params.minScore !== undefined) {
      conditions.push(
        vectorMinScoreCondition(distance, params.metric, params.minScore),
      );
    }
    // Candidate pushdown keeps the HNSW scan (verified plan: HNSW Index Scan
    // -> Nested Loop probe of the nodes pkey -> Limit). Exact under
    // `hnsw.iterative_scan` (pgvector >= 0.8, applied by the backend);
    // bounded by `ef_search` on older pgvector — still strictly better than
    // ranking tombstones into top-k and dropping them post-hoc.
    //
    // Plain `IN (subquery)`: with fresh statistics the planner hashes it
    // (brute-force scans) or drives per-row probes of the nodes pkey
    // under the ordered HNSW scan — the plan-verified shape the liveness
    // pushdown was built on. Stale statistics degrade any membership
    // form; the answer is `store.refreshStatistics()` after bulk loads,
    // not a cleverer SQL shape.
    if (candidates !== undefined) {
      conditions.push(sql`${table}."node_id" IN (${candidates})`);
    }
    // Pagination is rank-relative: the scan fetches `limit + offset`
    // ordered candidates and OFFSET discards the leading page.
    const pageOffset = params.offset ?? 0;
    const pageClause = vectorPageClause(params.limit, params.offset);
    // IVFFlat's iterative scan only offers `relaxed_order` (no
    // strict_order mode), so under the backend-applied
    // `ivfflat.iterative_scan` the index may emit the candidate set
    // slightly out of distance order. Re-sort the bounded set inside a
    // MATERIALIZED wrapper — the fence stops the planner from collapsing
    // the sort back into the index scan's claimed ordering — and page in
    // the wrapper: an OFFSET inside the relaxed scan could discard the
    // wrong rows. Score is monotone in distance, so ordering by score per
    // the metric's direction restores exact ranking.
    if (slot.indexType === "ivfflat") {
      const direction =
        params.metric === "cosine" ? sql.raw("DESC") : sql.raw("ASC");
      const relaxedBody = sql`
        SELECT ${table}."node_id" AS node_id, ${score} AS score
        FROM ${table}
        WHERE ${sql.join(conditions, sql` AND `)}
        ORDER BY ${distance} ASC
        LIMIT ${params.limit + pageOffset}
      `;
      return sql`
        WITH tg_vec_relaxed AS MATERIALIZED (${relaxedBody})
        SELECT node_id, score FROM tg_vec_relaxed
        ORDER BY score ${direction}, node_id ASC
        ${pageClause}
      `;
    }
    return sql`
      SELECT ${table}."node_id" AS node_id, ${score} AS score
      FROM ${table}
      WHERE ${sql.join(conditions, sql` AND `)}
      ORDER BY ${distance} ASC
      ${pageClause}
    `;
  },

  distanceExpression(embeddingColumn, queryEmbedding, metric) {
    return distanceExpression(embeddingColumn, queryEmbedding, metric);
  },

  buildCreateIndex(slot, options): SqlFragment | undefined {
    if (!usesAnnIndex(slot)) return undefined;
    return sql.raw(
      pgvectorIndexDdl(
        tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
        slot,
        options?.concurrent === true,
      ),
    );
  },

  buildDropIndex(slot): SqlFragment | undefined {
    if (!usesAnnIndex(slot)) return undefined;
    const indexName = pgvectorIndexName(slot);
    return sql.raw(`DROP INDEX IF EXISTS ${quoteIdentifier(indexName)}`);
  },

  buildDropStorage(slot): readonly string[] {
    const table = quoteIdentifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    // CASCADE drops the ANN index along with the table.
    return [`DROP TABLE IF EXISTS ${table} CASCADE`];
  },
};

function pgvectorIndexName(slot: VectorSlot): string {
  return vectorPhysicalName(
    INDEX_PREFIX,
    slot.graphId,
    slot.nodeKind,
    slot.fieldPath,
  );
}

function pgvectorIndexDdl(
  table: string,
  slot: VectorSlot,
  concurrent: boolean,
): string {
  const indexName = quoteIdentifier(pgvectorIndexName(slot));
  const quotedTable = quoteIdentifier(table);
  const opClass = operatorClass(slot.metric);
  // Honor the field's declared tuning; fall back to pgvector defaults.
  const m = slot.indexParams?.m ?? DEFAULT_HNSW_M;
  const efConstruction =
    slot.indexParams?.efConstruction ?? DEFAULT_HNSW_EF_CONSTRUCTION;
  const lists = slot.indexParams?.lists ?? DEFAULT_IVFFLAT_LISTS;
  // CONCURRENTLY builds without taking a write-blocking lock on the live table
  // (materializeIndexes passes concurrent on Postgres). Run outside a tx.
  const create = `CREATE INDEX${concurrent ? " CONCURRENTLY" : ""} IF NOT EXISTS`;

  switch (slot.indexType) {
    case "hnsw": {
      return `${create} ${indexName} ON ${quotedTable} USING hnsw ("embedding" ${opClass}) WITH (m = ${m}, ef_construction = ${efConstruction});`;
    }
    case "ivfflat": {
      return `${create} ${indexName} ON ${quotedTable} USING ivfflat ("embedding" ${opClass}) WITH (lists = ${lists});`;
    }
    case "none": {
      throw new Error(
        "pgvectorIndexDdl called for a brute-force-only slot (indexType 'none').",
      );
    }
    default: {
      const _exhaustive: never = slot.indexType;
      throw new Error(`Unsupported vector index type: ${String(_exhaustive)}`);
    }
  }
}
